import { Cat, KINDS } from '../../../gameplay/registry';
import { C } from '../../../gameplay/types';
import { TICK_RATE } from '../../../gameplay/stats';
import { FX_ONE } from '../fixed';
import type { World } from '../world';

export type VictoryType = 'conquest' | 'wonder' | 'territorial' | 'economic' | 'timeLimit';

export interface VictoryConfig {
  conditions: VictoryType[];
  /** Seconds a wonder must stand. */
  wonderSeconds: number;
  /** Seconds strategic points must be held. */
  territorySeconds: number;
  /** Total resources gathered for economic victory. */
  economicTarget: number;
  /** Time limit in seconds (score decides). */
  timeLimitSeconds: number;
}

export const DEFAULT_VICTORY: VictoryConfig = {
  conditions: ['conquest', 'wonder'],
  wonderSeconds: 900,
  territorySeconds: 240,
  economicTarget: 30000,
  timeLimitSeconds: 3600,
};

export interface VictoryState {
  /** Per team: tick at which a wonder was completed, or -1. */
  wonderSince: Map<number, number>;
  /** Strategic point positions (fixed point) and their controlling team. */
  points: { x: number; y: number; team: number }[];
  /** Team currently holding all points and since when. */
  territoryTeam: number;
  territorySince: number;
}

export function createVictoryState(points: { x: number; y: number }[]): VictoryState {
  return { wonderSince: new Map(), points: points.map((p) => ({ ...p, team: -1 })), territoryTeam: -1, territorySince: -1 };
}

/** Weighted score used for time-limit victory and the post-game summary. */
export function playerScore(w: World, pid: number): { economy: number; military: number; technology: number; territory: number; buildings: number; total: number } {
  const p = w.players[pid];
  const economy = Math.trunc(p.gathered.reduce((a, b) => a + b, 0) / FX_ONE / 10);
  const military = p.kills * 10;
  const technology = p.researched.reduce((a, b) => a + b, 0) * 20 + p.age * 200;
  let buildings = 0;
  for (let s = 0; s < w.highSlot; s++) if (w.alive[s] && w.owner[s] === pid && w.cat[s] === Cat.Building && w.built[s]) buildings += 5;
  const territory = 0;
  return { economy, military, technology, territory, buildings, total: economy + military + technology + territory + buildings };
}

export function updateVictory(w: World, cfg: VictoryConfig, vs: VictoryState): void {
  if (w.gameOver || w.tick % 10 !== 0) return;
  const np = w.players.length;
  const hasUnits = new Uint8Array(np);
  const hasBuildings = new Uint8Array(np);
  const wonderTeams = new Set<number>();
  for (let s = 0; s < w.highSlot; s++) {
    if (!w.alive[s]) continue;
    const o = w.owner[s];
    if (o < 0) continue;
    // A player is still in the game while they can build or fight: villagers, combat units, or a
    // finished building that trains units. Fishing boats, trade carts, houses, towers, walls and
    // bare foundations alone do not keep a beaten player alive.
    if (w.cat[s] === Cat.Unit || w.cat[s] === Cat.Held) {
      if (KINDS[w.kind[s]].unit!.classes & (C.Villager | C.Military)) hasUnits[o] = 1;
    } else if (w.cat[s] === Cat.Building) {
      const b = KINDS[w.kind[s]].building!;
      // (Before rules v5 the University trained nothing, so it kept no one in the game.)
      if (w.built[s] && b.trains && b.trains.some((k) => k !== 'engineer' || w.rules >= 5)) hasBuildings[o] = 1;
      if (b.key === 'wonder' && w.built[s]) wonderTeams.add(w.players[o].team);
    }
  }
  for (const p of w.players) {
    if (p.defeated) continue;
    if (p.resigned || (!hasUnits[p.id] && !hasBuildings[p.id])) {
      p.defeated = true;
      w.events.push({ e: 'defeated', owner: p.id });
      for (let s = 0; s < w.highSlot; s++) if (w.alive[s] && w.owner[s] === p.id) w.kill(s);
    }
  }
  const aliveTeams = new Set(w.players.filter((p) => !p.defeated).map((p) => p.team));
  const conds = cfg.conditions;
  const win = (team: number, reason: string) => {
    w.gameOver = true;
    w.winnerTeam = team;
    w.events.push({ e: 'victory', team, reason });
  };
  if (conds.includes('conquest') && aliveTeams.size <= 1) {
    win(aliveTeams.size === 1 ? [...aliveTeams][0] : -1, 'Conquest');
    return;
  }
  // Once every human player is out (resigned or defeated) the match is over for everyone: computer
  // players do not play on alone. The side with the highest score among those left wins.
  const humans = w.players.filter((p) => !p.isAI);
  if (humans.length > 0 && humans.every((p) => p.defeated)) {
    win(bestTeam(w), 'Last human out');
    return;
  }
  if (conds.includes('wonder')) {
    for (const t of [...vs.wonderSince.keys()]) if (!wonderTeams.has(t)) vs.wonderSince.delete(t);
    for (const t of wonderTeams) if (!vs.wonderSince.has(t)) vs.wonderSince.set(t, w.tick);
    for (const [t, since] of vs.wonderSince) {
      if (w.tick - since >= cfg.wonderSeconds * TICK_RATE) {
        win(t, 'Wonder');
        return;
      }
    }
  }
  if (conds.includes('territorial') && vs.points.length > 0) {
    for (const pt of vs.points) {
      const teams = new Set<number>();
      w.forUnitsInRadius(pt.x, pt.y, 3 * FX_ONE, (o) => {
        const ow = w.owner[o];
        if (ow >= 0 && KINDS[w.kind[o]].unit!.classes & C.Military) teams.add(w.players[ow].team);
      });
      if (teams.size === 1) pt.team = [...teams][0];
      else if (teams.size > 1) pt.team = -1;
    }
    const holder = vs.points[0].team;
    const all = holder >= 0 && vs.points.every((p) => p.team === holder);
    if (!all) vs.territoryTeam = -1;
    else if (vs.territoryTeam !== holder) {
      vs.territoryTeam = holder;
      vs.territorySince = w.tick;
    } else if (w.tick - vs.territorySince >= cfg.territorySeconds * TICK_RATE) {
      win(holder, 'Territorial');
      return;
    }
  }
  if (conds.includes('economic')) {
    for (const p of w.players) {
      if (p.defeated) continue;
      const total = p.gathered.reduce((a, b) => a + b, 0) / FX_ONE;
      if (total >= cfg.economicTarget) {
        win(p.team, 'Economic');
        return;
      }
    }
  }
  if (conds.includes('timeLimit') && w.tick >= cfg.timeLimitSeconds * TICK_RATE) {
    win(bestTeam(w), 'Time limit (score)');
  }
}

/** The team with the highest combined score among players still in the game (lowest team on a tie). */
function bestTeam(w: World): number {
  const teamScore = new Map<number, number>();
  for (const p of w.players) if (!p.defeated) teamScore.set(p.team, (teamScore.get(p.team) ?? 0) + playerScore(w, p.id).total);
  let best = -1;
  let bestS = -1;
  for (const [t, sc] of teamScore) if (sc > bestS || (sc === bestS && t < best)) {
    bestS = sc;
    best = t;
  }
  return best;
}
